import { Input, Form, Select } from 'antd'
import { useAtomValue } from 'jotai'
import { useCallback, type ComponentPropsWithoutRef, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

import {
  Button,
  Card,
  SearchForm,
  SearchFormItem,
  SearchFormRow,
  SearchFormSubmitButton,
  NormalizedDatePicker
} from '@/components/elements'
import {
  ACTIONS,
  DESCRIPTIONS,
  ERROR_MESSAGES,
  LABELS,
  STATUSES
} from '@/constants/common/message.constants'
import { CustomerSelect } from '@/features/customers/components'
import { SubPartnerSelect } from '@/features/partner-to-sub-partners/components'
import { PartnerSelect } from '@/features/partners/components'
import { SearchDateBasisSegmented } from '@/features/shared/components'
import { isClosingDateBasis } from '@/features/shared/models'
import { ShipmentCategorySelect } from '@/features/shipment-categories/components'
import { SiteAndAreaSelect } from '@/features/sites/components'
import { type UserMeExEntity } from '@/libs/api'
import {
  SHIPMENT_DELIVERY_STATUS_TYPE,
  SHIPMENT_DELIVERY_STATUS_TYPE_LABEL,
  type ShipmentDeliveryStatusType
} from '@/libs/api/types'
import { safeParseISO, safeFormatISO } from '@/libs/utils'

import {
  complementCreatedAtRange,
  mapToShipmentOrdersSearch,
  mapToShipmentOrdersSearchFormValue,
  type ShipmentOrdersSearch,
  type ShipmentOrdersSearchFormValue,
  validateCreatedAtRange,
  validateShippingDateRange
} from '../../-models'
import { selectedDcIdAtom } from '../../../-states'

/** 発送日の検証結果に対応するエラーメッセージ（#4567 / #4568） */
const shippingDateErrorMessage = (
  errorType: 'required' | 'reversed' | 'exceeded'
): string => {
  if (errorType === 'required') {
    return ERROR_MESSAGES.SHIPPING_DATE_REQUIRED
  }
  if (errorType === 'reversed') {
    return ERROR_MESSAGES.SHIPPING_DATE_RANGE_REVERSED
  }
  return ERROR_MESSAGES.SHIPPING_DATE_RANGE_EXCEEDED
}

/** 登録日の検証結果に対応するエラーメッセージ（#5285 VOC_PJ-506） */
const createdAtErrorMessage = (errorType: 'reversed' | 'exceeded'): string =>
  errorType === 'reversed'
    ? ERROR_MESSAGES.CREATED_AT_RANGE_REVERSED
    : ERROR_MESSAGES.CREATED_AT_RANGE_EXCEEDED

type Props = Omit<ComponentPropsWithoutRef<'div'>, 'onSubmit'> & {
  authUser: UserMeExEntity
  tenantKey: string
  search: ShipmentOrdersSearch
  defaultSearch: ShipmentOrdersSearch
  onSubmit: (search: ShipmentOrdersSearch) => void
  defaultDcId?: number | null
  preservedTake?: number
}

/**
 * 配送依頼一覧の検索フォーム
 */
export const ShipmentOrdersSearchForm: FC<Props> = ({
  authUser,
  tenantKey,
  search,
  defaultSearch,
  onSubmit,
  preservedTake,
  ...props
}) => {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    formState: { errors }
  } = useForm<ShipmentOrdersSearchFormValue>({
    values: mapToShipmentOrdersSearchFormValue(search)
  })

  const selectedDcId = useAtomValue(selectedDcIdAtom)

  const handleFinish = useCallback(() => {
    handleSubmit((submittedValue: ShipmentOrdersSearchFormValue) => {
      // 登録日は片側だけの指定を 12 か月幅へ補完してから検証する（#5285 §4.2-6）。
      // 補完は検索基準のモード分岐より前に行う。どちらのモードでも登録日は AND 条件で、
      // 片側のまま API へ送ると BE の両側必須検証で 400 になる
      const { from: createdAtFrom, to: createdAtTo } = complementCreatedAtRange(
        submittedValue.createdAtFrom,
        submittedValue.createdAtTo
      )
      // 補完した値を入力欄にも反映し、利用者が実際に検索した範囲を確認できるようにする
      setValue('createdAtFrom', createdAtFrom)
      setValue('createdAtTo', createdAtTo)

      const createdAtResult = validateCreatedAtRange(createdAtFrom, createdAtTo)
      if (!createdAtResult.isValid) {
        toast.error(createdAtErrorMessage(createdAtResult.errorType))
        return
      }

      const formValue: ShipmentOrdersSearchFormValue = {
        ...submittedValue,
        createdAtFrom,
        createdAtTo
      }
      // 登録日で母集合が絞られるので発送日の必須を外す（#5285 CF-3）。
      // BE の shouldValidateShippingDate の免除条件と同じ規則
      const hasCreatedAt = Boolean(createdAtFrom && createdAtTo)

      // 締日基準（請求月）モード: 年月のみ必須。荷主指定は任意（Q-7 = 7-A、改訂 (10)）。
      // 日付範囲は併用でき、指定されていれば AND で絞り込まれる（Q-8 = 8-A）
      if (isClosingDateBasis(formValue.searchDateBasis)) {
        if (!formValue.operatingYearMonth) {
          toast.error(ERROR_MESSAGES.OPERATING_YEAR_MONTH_REQUIRED)
          return
        }

        // 発送日は任意だが、指定するなら通常検索と同じ制約（From/To のペア・
        // 12 か月以内・逆転しない）を満たす必要がある。併用可能にした分、ここを
        // 素通りさせると片側だけ・期間超過のまま API に飛ぶ
        const rangeResult = validateShippingDateRange(
          formValue.shippingDateFrom,
          formValue.shippingDateTo,
          { isRequired: false }
        )
        if (!rangeResult.isValid) {
          toast.error(shippingDateErrorMessage(rangeResult.errorType))
          return
        }

        return onSubmit({
          ...mapToShipmentOrdersSearch(formValue),
          take: preservedTake,
          dcId: selectedDcId
        })
      }

      // 通常検索: 発送日のバリデーション
      const validationResult = validateShippingDateRange(
        formValue.shippingDateFrom,
        formValue.shippingDateTo,
        { isRequired: !hasCreatedAt }
      )

      if (!validationResult.isValid) {
        toast.error(shippingDateErrorMessage(validationResult.errorType))
        return
      }

      return onSubmit({
        ...mapToShipmentOrdersSearch(formValue),
        take: preservedTake,
        dcId: selectedDcId,
        shippingDateFrom: formValue.shippingDateFrom
          ? safeFormatISO(formValue.shippingDateFrom)
          : '',
        shippingDateTo: formValue.shippingDateTo
          ? safeFormatISO(formValue.shippingDateTo)
          : ''
      })
    })()
  }, [handleSubmit, onSubmit, preservedTake, selectedDcId, setValue])

  const handleCancel = useCallback(() => {
    // defaultSearchを使って初期化
    const resetValues = mapToShipmentOrdersSearchFormValue(defaultSearch)
    reset(resetValues)
    handleFinish()
  }, [reset, handleFinish, defaultSearch])

  return (
    <div {...props} data-testid="shipment-orders-search-form">
      <Card>
        <SearchForm onFinish={handleFinish}>
          <SearchFormRow>
            <Controller
              name={'searchDateBasis'}
              control={control}
              render={({ field: basisField }) => (
                <Controller
                  name={'operatingYearMonth'}
                  control={control}
                  render={({ field: yearMonthField }) => (
                    <SearchFormItem
                      label={LABELS.SEARCH_DATE_BASIS}
                      htmlFor={basisField.name}
                    >
                      <SearchDateBasisSegmented
                        id={basisField.name}
                        value={basisField.value}
                        onChange={basisField.onChange}
                        yearMonth={yearMonthField.value}
                        onYearMonthChange={yearMonthField.onChange}
                        showExpense={false}
                      />
                    </SearchFormItem>
                  )}
                />
              )}
            />
            <Controller
              name={'status'}
              control={control}
              render={({ field }) => (
                <SearchFormItem label={LABELS.STATUS} htmlFor={field.name}>
                  <Select
                    {...field}
                    mode="multiple"
                    allowClear
                    placeholder={STATUSES.UNSELECTED}
                    id={field.name}
                    onChange={(value: ShipmentDeliveryStatusType[]) => {
                      field.onChange(value)
                    }}
                    className="min-w-[200px]"
                    maxTagCount="responsive"
                    options={Object.entries(SHIPMENT_DELIVERY_STATUS_TYPE).map(
                      ([, value]) => ({
                        value,
                        label:
                          SHIPMENT_DELIVERY_STATUS_TYPE_LABEL[
                            value as ShipmentDeliveryStatusType
                          ]
                      })
                    )}
                  />
                </SearchFormItem>
              )}
            />
            <Controller
              name={'isManual'}
              control={control}
              render={({ field }) => (
                <SearchFormItem
                  label={LABELS.HOW_TO_CREATE_SHIPMENT_ORDER}
                  htmlFor={field.name}
                >
                  <Select
                    {...field}
                    id={field.name}
                    // デフォルトは「すべて」を表示するためallowClearは使わない
                    onChange={(value) => {
                      field.onChange((value ?? null) as boolean | null)
                    }}
                    className="min-w-[200px]"
                    options={[
                      { value: null, label: LABELS.ALL },
                      { value: true, label: LABELS.ONLY_MANUAL },
                      { value: false, label: LABELS.ONLY_IMPORT }
                    ]}
                  />
                </SearchFormItem>
              )}
            />
            <Controller
              name={'orderNo'}
              control={control}
              render={({ field }) => (
                <SearchFormItem label={LABELS.ORDER_NO} htmlFor={field.name}>
                  <Input
                    {...field}
                    className="w-[140px]"
                    id={field.name}
                    placeholder="20240101-0001-1"
                  />
                </SearchFormItem>
              )}
            />
            <Controller
              name={'customKeyFreeWord'}
              control={control}
              render={({ field }) => (
                <SearchFormItem
                  label={LABELS.CUSTOM_KEY_FREE_WORD}
                  htmlFor={field.name}
                >
                  <Input
                    {...field}
                    className="w-[150px]"
                    id={field.name}
                    placeholder={LABELS.CUSTOM_KEY_FREE_WORD}
                  />
                </SearchFormItem>
              )}
            />
            <div className="flex">
              <Controller
                name={'shippingDateFrom'}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <SearchFormItem
                    label={LABELS.SHIPPING_DATE}
                    htmlFor={field.name}
                    tooltip={DESCRIPTIONS.SHIPPING_DATE_RANGE_TOOLTIP}
                  >
                    <Form.Item
                      htmlFor={field.name}
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0 w-[135px] align-middle"
                      style={{ display: 'inline-block', marginBottom: 0 }}
                    >
                      <NormalizedDatePicker
                        id={field.name}
                        placeholder={STATUSES.UNSELECTED}
                        value={safeParseISO(field.value)}
                        onInvalidText={(msg) => {
                          setValue('shippingDateFrom', null, {
                            shouldValidate: false,
                            shouldDirty: true
                          })
                          setError(
                            'shippingDateFrom',
                            { type: 'manual', message: msg },
                            { shouldFocus: true }
                          )
                        }}
                        onValidText={() => clearErrors('shippingDateFrom')}
                        onChange={(date) => {
                          field.onChange(date)
                        }}
                      />
                    </Form.Item>
                  </SearchFormItem>
                )}
              />
              <span className="mx-1 mt-8">〜</span>
              <Controller
                name={'shippingDateTo'}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <SearchFormItem
                    className="pt-[26px]"
                    label={null}
                    htmlFor={field.name}
                  >
                    <Form.Item
                      htmlFor={field.name}
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0 w-[135px]"
                    >
                      <NormalizedDatePicker
                        id={field.name}
                        placeholder={STATUSES.UNSELECTED}
                        value={safeParseISO(field.value)}
                        onInvalidText={(msg) => {
                          setValue('shippingDateTo', null, {
                            shouldValidate: false,
                            shouldDirty: true
                          })
                          setError(
                            'shippingDateTo',
                            { type: 'manual', message: msg },
                            { shouldFocus: true }
                          )
                        }}
                        onValidText={() => clearErrors('shippingDateTo')}
                        onChange={(date) => {
                          field.onChange(date)
                        }}
                      />
                    </Form.Item>
                  </SearchFormItem>
                )}
              />
            </div>
            <div className="flex">
              <Controller
                name={'deliveryDateFrom'}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <SearchFormItem
                    label={LABELS.DELIVERY_DATE}
                    htmlFor={field.name}
                  >
                    <Form.Item
                      htmlFor={field.name}
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0 w-[135px] align-middle"
                      style={{ display: 'inline-block', marginBottom: 0 }}
                    >
                      <NormalizedDatePicker
                        id={field.name}
                        placeholder={STATUSES.UNSELECTED}
                        value={safeParseISO(field.value)}
                        onInvalidText={(msg) => {
                          setValue('deliveryDateFrom', null, {
                            shouldValidate: false,
                            shouldDirty: true
                          })
                          setError(
                            'deliveryDateFrom',
                            { type: 'manual', message: msg },
                            { shouldFocus: true }
                          )
                        }}
                        onValidText={() => clearErrors('deliveryDateFrom')}
                        onChange={(date) => {
                          field.onChange(date)
                        }}
                      />
                    </Form.Item>
                  </SearchFormItem>
                )}
              />
              <span className="mx-1 mt-8">〜</span>
              <Controller
                name={'deliveryDateTo'}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <SearchFormItem
                    className="pt-[26px]"
                    label={null}
                    htmlFor={field.name}
                  >
                    <Form.Item
                      htmlFor={field.name}
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0 w-[135px]"
                    >
                      <NormalizedDatePicker
                        id={field.name}
                        placeholder={STATUSES.UNSELECTED}
                        value={safeParseISO(field.value)}
                        onInvalidText={(msg) => {
                          setValue('deliveryDateTo', null, {
                            shouldValidate: false,
                            shouldDirty: true
                          })
                          setError(
                            'deliveryDateTo',
                            { type: 'manual', message: msg },
                            { shouldFocus: true }
                          )
                        }}
                        onValidText={() => clearErrors('deliveryDateTo')}
                        onChange={(date) => {
                          field.onChange(date)
                        }}
                      />
                    </Form.Item>
                  </SearchFormItem>
                )}
              />
            </div>
            {/*
              登録日（#5285 VOC_PJ-506）。片側だけの指定は submit 時に 12ヶ月幅へ補完する。
              一覧の列順（発送日 → 着日 → 登録日）と並びを揃えている
            */}
            <div className="flex">
              <Controller
                name={'createdAtFrom'}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <SearchFormItem
                    label={LABELS.CREATED_DATE}
                    htmlFor={field.name}
                    tooltip={DESCRIPTIONS.CREATED_AT_RANGE_TOOLTIP}
                  >
                    <Form.Item
                      htmlFor={field.name}
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0 w-[135px] align-middle"
                      style={{ display: 'inline-block', marginBottom: 0 }}
                    >
                      <NormalizedDatePicker
                        id={field.name}
                        placeholder={STATUSES.UNSELECTED}
                        value={safeParseISO(field.value)}
                        onInvalidText={(msg) => {
                          setValue('createdAtFrom', null, {
                            shouldValidate: false,
                            shouldDirty: true
                          })
                          setError(
                            'createdAtFrom',
                            { type: 'manual', message: msg },
                            { shouldFocus: true }
                          )
                        }}
                        onValidText={() => clearErrors('createdAtFrom')}
                        onChange={(date) => {
                          field.onChange(date)
                        }}
                      />
                    </Form.Item>
                  </SearchFormItem>
                )}
              />
              <span className="mx-1 mt-8">〜</span>
              <Controller
                name={'createdAtTo'}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <SearchFormItem
                    className="pt-[26px]"
                    label={null}
                    htmlFor={field.name}
                  >
                    <Form.Item
                      htmlFor={field.name}
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0 w-[135px]"
                    >
                      <NormalizedDatePicker
                        id={field.name}
                        placeholder={STATUSES.UNSELECTED}
                        value={safeParseISO(field.value)}
                        onInvalidText={(msg) => {
                          setValue('createdAtTo', null, {
                            shouldValidate: false,
                            shouldDirty: true
                          })
                          setError(
                            'createdAtTo',
                            { type: 'manual', message: msg },
                            { shouldFocus: true }
                          )
                        }}
                        onValidText={() => clearErrors('createdAtTo')}
                        onChange={(date) => {
                          field.onChange(date)
                        }}
                      />
                    </Form.Item>
                  </SearchFormItem>
                )}
              />
            </div>
            <Controller
              name={'customerIds'}
              control={control}
              render={({ field }) => (
                <SearchFormItem label={LABELS.CUSTOMER} htmlFor={field.name}>
                  <CustomerSelect
                    {...field}
                    authUser={authUser}
                    tenantKey={tenantKey}
                    mode="multiple"
                    id={field.name}
                    popupMatchSelectWidth={false}
                    allowClear
                    onChange={(value) => {
                      field.onChange(value)
                    }}
                    className="min-w-[280px]"
                    hideCreateButton={true}
                  />
                </SearchFormItem>
              )}
            />
            <Controller
              name={'partnerIds'}
              control={control}
              render={({ field }) => (
                <SearchFormItem
                  label={LABELS.DESIGNATED_PARTNER}
                  htmlFor={field.name}
                >
                  <PartnerSelect
                    {...field}
                    mode="multiple"
                    tenantKey={tenantKey}
                    popupMatchSelectWidth={false}
                    id={field.name}
                    onChange={(value) => {
                      field.onChange(value)
                    }}
                  />
                </SearchFormItem>
              )}
            />
            <Controller
              name={'dispatchPartnerIds'}
              control={control}
              render={({ field }) => (
                <SearchFormItem label={LABELS.PARTNER} htmlFor={field.name}>
                  <PartnerSelect
                    {...field}
                    mode="multiple"
                    tenantKey={tenantKey}
                    popupMatchSelectWidth={false}
                    id={field.name}
                    onChange={(value) => {
                      field.onChange(value)
                    }}
                  />
                </SearchFormItem>
              )}
            />
            <Controller
              name={'dispatchSubPartnerIds'}
              control={control}
              render={({ field }) => (
                <SearchFormItem label={LABELS.SUB_PARTNER} htmlFor={field.name}>
                  <SubPartnerSelect
                    {...field}
                    mode="multiple"
                    tenantKey={tenantKey}
                    popupMatchSelectWidth={false}
                    id={field.name}
                    placeholder={STATUSES.UNSELECTED}
                    onChange={(value) => {
                      field.onChange(value)
                    }}
                  />
                </SearchFormItem>
              )}
            />
            <Controller
              name={'dispatchTruckCode'}
              control={control}
              render={({ field }) => (
                <SearchFormItem label={LABELS.TRUCK_CODE} htmlFor={field.name}>
                  <Input
                    {...field}
                    className="w-[140px]"
                    id={field.name}
                    placeholder="0000"
                  />
                </SearchFormItem>
              )}
            />
            <Controller
              name={'shipmentCategoryIds'}
              control={control}
              render={({ field }) => (
                <SearchFormItem
                  label={LABELS.SHIPMENT_CATEGORY}
                  htmlFor={field.name}
                >
                  <ShipmentCategorySelect
                    tenantKey={tenantKey}
                    {...field}
                    id={field.name}
                    mode="multiple"
                    onChange={(value) => {
                      field.onChange(value)
                    }}
                    className="w-[180px]"
                    hideCreateButton={true}
                  />
                </SearchFormItem>
              )}
            />
            <Controller
              name={'originSiteIds'}
              control={control}
              render={({ field }) => (
                <SearchFormItem label={LABELS.ORIGIN} htmlFor={field.name}>
                  <SiteAndAreaSelect
                    {...field}
                    tenantKey={tenantKey}
                    showSearch
                    mode="multiple"
                    placeholder={STATUSES.UNSELECTED}
                    id={field.name}
                    allowClear
                    onChange={(value) => {
                      field.onChange(value)
                    }}
                    className="w-[180px]"
                  />
                </SearchFormItem>
              )}
            />
            <Controller
              name={'destSiteIds'}
              control={control}
              render={({ field }) => (
                <SearchFormItem label={LABELS.DESTINATION} htmlFor={field.name}>
                  <SiteAndAreaSelect
                    {...field}
                    tenantKey={tenantKey}
                    showSearch
                    mode="multiple"
                    placeholder={STATUSES.UNSELECTED}
                    id={field.name}
                    allowClear
                    onChange={(value) => {
                      field.onChange(value)
                    }}
                    className="w-[180px]"
                  />
                </SearchFormItem>
              )}
            />
          </SearchFormRow>
          <SearchFormRow className="justify-end">
            <Button type="text" onClick={handleCancel}>
              {ACTIONS.RESET}
            </Button>
            <SearchFormSubmitButton errors={errors} />
          </SearchFormRow>
        </SearchForm>
      </Card>
    </div>
  )
}
